import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useHatifWebhookCacheRouter } from "@/hooks/useHatifWebhookCacheRouter";
import { IS_DEV_MODE } from "@/services/api";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: ReactNode;
}

// IS_DEV_MODE is true only when VITE_DEV_MODE=true is injected at build time.
// This is set in the development environment only (never staging or production).
// Note: import.meta.env.PROD is true for ALL Vite builds (including dev.dallali.com),
// so it cannot be used to distinguish environments — use VITE_ENV instead if needed.
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, isLoading, isRefreshing } = useAuth();
  const location = useLocation();

  // Company-wide Hatif webhook subscription, mounted at the authed shell
  // so a single channel-per-tab fans out to every cached conversation
  // (active page + cached but inactive). Internal no-op until companyId
  // resolves; safe to run during the loading / unauthed branches below.
  useHatifWebhookCacheRouter();

  // Dev bypass: allow access without a real session when VITE_DEV_MODE=true
  if (IS_DEV_MODE) {
    return <>{children}</>;
  }

  // While a token refresh is in flight (focus/scheduled), WAIT — don't bounce a
  // focused/stale tab to /auth before the rotated token lands. The refresh is
  // hard-capped (AUTH_REFRESH_GRACE_MS), so this can't spin forever: it either
  // resolves (user stays) or genuinely fails (signOut → user null → /auth).
  if (isLoading || isRefreshing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
